import React from 'react';
import classNames from 'classnames';
import { useIntl } from 'react-intl';
import { useField } from 'react-final-form';

import { GET_MEDIA_UPLOAD_URL } from 'constants/url';

import { DocumentStatus } from 'modules/selfServices/types/SelfService';

import { useSelector } from 'hooks';
import useUpload from 'hooks/useUpload';
import { required } from 'utils/formUtils';
import useLabelTranslation from 'hooks/useLabelTranslation';

import { getSelectedSelfServiceId } from 'modules/auth/selectors';
import { getMobilityDocumentdMediaSupport } from 'modules/refData/selectors';

import Icon from 'components/ui/Icon';
import Image from 'components/ui/Image';
import Spinner from 'components/ui/Spinner';
import Carousel from 'components/ui/Carousel';
import Button, { ButtonType } from 'components/ui/Button';

import { MediaEntityType, MediaSubType } from '../../types/MediaTypeEnum';
import MandatorySymbol from '../ui/MandatorySymbol';

interface MobilityDocumentProps {
  name: string;
}

const Document: React.FC<MobilityDocumentProps> = ({ name }) => {
  const intl = useIntl();
  const { getLabelTranslation } = useLabelTranslation();

  const selfServiceId = useSelector(getSelectedSelfServiceId);
  const supportedTypes = useSelector(getMobilityDocumentdMediaSupport);

  const [mediasFullscreen, setMediasFullscreen] = React.useState([]);
  const [hideFullscreenAction, setHideFullscreenAction] = React.useState(true);

  const { value: document } = useField(name, { subscription: { value: true } }).input;

  const {
    name: documentName, status, comment, sample,
  } = document;
  const isRequested = status === DocumentStatus.REQUESTED;

  const { input: { value: media, onChange }, meta } = useField(`${name}.media`, {
    format: (v) => v,
    subscription: { value: true, error: true, submitFailed: true },
    validate: (value) => (isRequested ? required(value) : undefined),
  });

  const error = meta.submitFailed && meta.error && intl.formatMessage({ id:meta.error, defaultMessage: meta.error });

  const {
    upload, isLoading: isUploading, remove, isDeleting, browse, medias: previewMedias,
  } = useUpload({ selfServiceId, initialMedias: media ? [media] : undefined });
  const mediaPath = media?.path ?? sample?.path;

  const isLoading = isUploading || isDeleting;
  const canUpload = isRequested && !media;
  const displayFullscreen = mediasFullscreen.length > 0;
  const displayDelete = media && isRequested;

  const handlePreviewMedia = React.useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    const hasUploadedMedia = previewMedias.length > 0;
    setMediasFullscreen(hasUploadedMedia ? previewMedias : [sample]);
    setHideFullscreenAction(!hasUploadedMedia || !isRequested);
  }, [isRequested, previewMedias, sample]);

  const handleClose = React.useCallback(() => {
    setMediasFullscreen([]);
  }, []);

  const handleUpload = async () => {
    if (canUpload) {
      try {
        const [file] = Array.from(await browse({ accept: supportedTypes, capture: 'environment' }));

        if (file) {
          const { uploadId, type, path } = await upload({
            file,
            contentType: file.type,
            url: GET_MEDIA_UPLOAD_URL,
            entityType: MediaEntityType.MOBILITY,
            subType: MediaSubType.DOCUMENT_MEDIA,
          });

          onChange({ uploadId, type, path });
        }
      } catch { /* Do nothing */ }
    }
  };

  const onDeleteMedia = async () => {
    setMediasFullscreen([]);

    await remove(media.uploadId);

    onChange(undefined);
  };

  const onDelete = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onDeleteMedia();
  };

  return (
    <>
      {displayFullscreen && (
        <Carousel
          fullscreen
          medias={mediasFullscreen}
          onClose={handleClose}
          onDelete={onDeleteMedia}
          hideActions={hideFullscreenAction}
          watermarkTitle={hideFullscreenAction ? intl.formatMessage({
            id: 'mobilityDocument.sample',
            defaultMessage: 'Sample picture',
          }) : undefined}
        />
      )}
      <div
        data-testid={name}
        onClick={handleUpload}
        className={classNames(
          'flex flex-row items-center justify-between text-lowest py-6 mx-5 overflow-hidden',
          {
            'cursor-pointer': canUpload,
            'text-default': Boolean(media),
          },

        )}
      >
        <div className="flex flex-auto items-center w-2/5">
          <div
            data-testId="preview"
            onClick={mediaPath ? handlePreviewMedia : undefined}
            className="mr-5 size-10 border-4 rounded cursor-pointer relative overflow-hidden flex items-center justify-center"
            role="button"
            tabIndex={0}
            aria-label={
              mediaPath
                ? intl.formatMessage({ id: 'accessibility.button.viewMedia', defaultMessage: 'View the media' })
                : intl.formatMessage({ id: 'accessibility.button.upload', defaultMessage: 'Upload a new media' })}
          >
            {mediaPath && <Image {...media} thumbnail src={mediaPath} className="size-8 bg-cover" />}
            {!mediaPath && <Icon className="fb-icon--media text-2xl kiosk:text-4xl text-lowest"/>}
          </div>
          <div className="flex flex-none items-start">
            {isRequested && <MandatorySymbol/>}
            <div className="flex flex-col h-full">
              <span className="font-bold">
                {getLabelTranslation(documentName)}
              </span>
              {error &&
                  <span className="text-danger-high px-1 !text-xs font-normal block">
                    {error}
                  </span>
              }
              {comment && (
                <span className="font-medium italic text-low">
                  {comment}
                </span>
              )}
            </div>
          </div>
        </div>
        {isLoading && <Spinner className="mx-0 h-8" />}
        {!isLoading && (
          <div className="flex flex-none justify-end">
            {canUpload &&
              <Button className="!p-0" type={ButtonType.ICON} aria-label={intl.formatMessage({ id: 'accessibility.button.upload', defaultMessage: 'Upload a new media' })}>
                <Icon className="fb-icon--camera text-2xl kiosk:text-4xl text-default"/>
              </Button>}
            {displayDelete && (
              <Button className="ml-4 !p-0 flex" onClick={onDelete} testId="delete" type={ButtonType.LIGHT} aria-label={intl.formatMessage({ id: 'accessibility.button.deleteMedia', defaultMessage: 'Delete the media' })}>
                <Icon className="fb-icon--delete text-2xl kiosk:text-4xl text-default"/>
              </Button>
            )}
          </div>
        )}
      </div>
    </>

  );
};

export default Document;
